import { Chapter } from "./Chapter";
import { Reveal } from "./Reveal";
import { PawWatermark } from "./Paws";
import { photos } from "../data/siteConfig";
import { Sparkles, Pill, Feather } from "lucide-react";

const CARE_CARDS = [
    {
        icon: Sparkles,
        title: "Elderly cats",
        text: "We've spent years caring for older cats — patient routines, gentle handling, and the quiet, calm environment seniors appreciate. Your old friend is in experienced hands.",
        testid: "care-elderly",
    },
    {
        icon: Pill,
        title: "Medication, carefully given",
        text: "Experienced with pills and tablets, prescribed ear creams, oral liquids and medication given with food — always exactly to your and your vet's instructions. Just talk it through with us when you book.",
        testid: "care-medication",
    },
    {
        icon: Feather,
        title: "Nervous & solitary cats",
        text: "Some cats find new places stressful, or simply prefer their own company. We give them extra time, extra space and — when it suits — the privacy of The Penthouse.",
        testid: "care-nervous",
    },
];

const RHYTHM = [
    "Accommodation cleaned daily",
    "Floors cleaned daily",
    "Fresh water, always",
    "Beds & blankets kept clean and refreshed",
    "Continual checks, feeding and attention",
    "Cared for seven days a week — well beyond visitor hours",
];

export const Care = () => (
    <section
        className="relative overflow-hidden bg-coal py-24 text-cream sm:py-32"
        data-testid="care-section"
    >
        <PawWatermark tone="dark" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
            <Chapter
                no="03"
                id="care"
                dark
                eyebrow="Care & Wellbeing"
                title={
                    <>
                        Cleaning, feeding, checking{" "}
                        <em>and cuddles.</em>
                    </>
                }
                intro="Safety, cleanliness, happiness and care — in that order, every day. Some cats arrive confident, some arrive nervous; some want attention, some want space. We read the cat in front of us."
            />

            <div className="mt-16 grid gap-12 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <Reveal>
                        <div
                            className="rounded-3xl border border-[#7C8966]/25 bg-[#2A2D24] p-8 sm:p-10"
                            data-testid="care-rhythm-card"
                        >
                            <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone-400">
                                The daily rhythm
                            </p>
                            <ul className="mt-6 space-y-4">
                                {RHYTHM.map((r) => (
                                    <li
                                        key={r}
                                        className="flex items-start gap-3 text-sm leading-relaxed text-stone-300 sm:text-base"
                                    >
                                        <span
                                            className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-stone-500"
                                            aria-hidden="true"
                                        />
                                        {r}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>

                    {/* Intimate relaxed-cat portrait (when supplied) */}
                    {photos.care && (
                        <Reveal delay={0.12}>
                            <div
                                className="spotlight-frame group mt-6 overflow-hidden rounded-3xl border border-[#7C8966]/25"
                                data-testid="care-photo"
                            >
                                <img
                                    src={photos.care.src}
                                    alt={photos.care.alt}
                                    loading="lazy"
                                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                                />
                            </div>
                        </Reveal>
                    )}
                </div>

                <div className="space-y-5 lg:col-span-7">
                    {CARE_CARDS.map((c, i) => (
                        <Reveal key={c.title} delay={0.07 * i}>
                            <article
                                className="group flex gap-6 rounded-2xl border border-[#7C8966]/25 bg-[#2A2D24] p-7 transition-[border-color,transform,background-color] duration-300 hover:-translate-y-1 hover:border-stone-400/40"
                                data-testid={c.testid}
                            >
                                <c.icon
                                    className="h-8 w-8 shrink-0 text-euca transition-transform duration-300 group-hover:scale-110"
                                    aria-hidden="true"
                                />
                                <div>
                                    <h3 className="font-serif text-2xl font-semibold text-cream">
                                        {c.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-stone-300 sm:text-base">
                                        {c.text}
                                    </p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                    <Reveal delay={0.2}>
                        <p
                            className="pl-1 font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-stone-500"
                            data-testid="care-disclaimer"
                        >
                            We're experienced carers, not vets — medication is
                            given strictly to your instructions and your
                            veterinarian's.
                        </p>
                    </Reveal>
                </div>
            </div>
        </div>
    </section>
);
